import { SegmentEnum, SubSegmentEnum } from "../../lib/types/runtimeTypes";
import { type NestedMessagePaths, type Segment, type SubSegment } from "../../lib/types/types";

const SegmentAcceptableUploads: Record<SegmentEnum, NestedMessagePaths[]> = {
  [SegmentEnum.STUDENT]: [
    "step.docUpload.acceptableUploads.student.idCard",
    "step.docUpload.acceptableUploads.student.classSchedule",
    "step.docUpload.acceptableUploads.student.tuitionReceipt",
  ],
  [SegmentEnum.TEACHER]: [
    "step.docUpload.acceptableUploads.officialLetter",
    "step.docUpload.acceptableUploads.teacher.idCard",
    "step.docUpload.acceptableUploads.teacher.payStub",
  ],
  [SegmentEnum.MEDICAL]: [
    "step.docUpload.acceptableUploads.medical.idCard",
    "step.docUpload.acceptableUploads.medical.payStub",
    "step.docUpload.acceptableUploads.medical.letterEmployment",
  ],
  [SegmentEnum.FIRST_RESPONDER]: [
    "step.docUpload.acceptableUploads.firstResponder.idCard",
    "step.docUpload.acceptableUploads.firstResponder.letter",
    "step.docUpload.acceptableUploads.firstResponder.payStub",
  ],
  [SegmentEnum.MILITARY]: [],
  [SegmentEnum.MEMBER]: [
    "step.docUpload.acceptableUploads.member.idCard",
    "step.docUpload.acceptableUploads.member.payStub",
  ],
  [SegmentEnum.SENIOR]: [
    "step.docUpload.acceptableUploads.senior.birthCertificate",
    "step.docUpload.acceptableUploads.senior.driversLicense",
    "step.docUpload.acceptableUploads.senior.passport",
    "step.docUpload.acceptableUploads.senior.stateId",
  ],
  [SegmentEnum.AGE]: [
    "step.docUpload.acceptableUploads.senior.birthCertificate",
    "step.docUpload.acceptableUploads.senior.driversLicense",
    "step.docUpload.acceptableUploads.senior.passport",
    "step.docUpload.acceptableUploads.senior.stateId",
  ],
  [SegmentEnum.EMPLOYMENT]: [
    "step.docUpload.acceptableUploads.employment.employeeIdCard",
    "step.docUpload.acceptableUploads.employment.payStub",
    "step.docUpload.acceptableUploads.employment.officialLetter",
  ],
  [SegmentEnum.IDENTITY]: [],
  [SegmentEnum.LICENSED_PROFESSIONAL]: [],
  [SegmentEnum.RECENT_MOVER]: [
    "step.docUpload.acceptableUploads.recentMover.changeOfAddress",
    "step.docUpload.acceptableUploads.recentMover.mortgageDeed",
    "step.docUpload.acceptableUploads.recentMover.rentalAgreement",
  ],
  [SegmentEnum.LOW_INCOME]: [
    "step.docUpload.acceptableUploads.lowIncome.program",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalLetter",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalScreenshot",
    "step.docUpload.acceptableUploads.lowIncome.programBankStatement",
  ],
};

const SubSegmentAcceptableUploads: Record<SubSegmentEnum, NestedMessagePaths[]> = {
  [SubSegmentEnum.ACTIVE_DUTY]: [
    "step.docUpload.acceptableUploads.military.active.leaveEarningStatement",
    "step.docUpload.acceptableUploads.military.active.uniformedServicesId",
  ],
  [SubSegmentEnum.VETERAN]: [
    "step.docUpload.acceptableUploads.military.vet.dd214Form",
    "step.docUpload.acceptableUploads.military.vet.dischargeCertificate",
    "step.docUpload.acceptableUploads.military.vet.vaHealthBenefitsCard",
    "step.docUpload.acceptableUploads.military.vet.vaDigitalId",
    "step.docUpload.acceptableUploads.military.vet.driverLicenseVeteran",
    "step.docUpload.acceptableUploads.military.vet.departmentVeteranId",
  ],
  [SubSegmentEnum.RESERVIST]: [
    "step.docUpload.acceptableUploads.military.reservist.leaveEarningStatement",
    "step.docUpload.acceptableUploads.military.reservist.uniformedServicesId",
  ],
  [SubSegmentEnum.MILITARY_RETIREE]: [
    "step.docUpload.acceptableUploads.military.militaryRetiree.blueUniformedId",
    "step.docUpload.acceptableUploads.military.militaryRetiree.vaHealthBenefitsCard",
    "step.docUpload.acceptableUploads.military.militaryRetiree.retireeId",
    "step.docUpload.acceptableUploads.military.militaryRetiree.dd214Form",
    "step.docUpload.acceptableUploads.military.militaryRetiree.vaDigitalId",
  ],
  [SubSegmentEnum.MILITARY_FAMILY]: [
    "step.docUpload.acceptableUploads.military.dependent", // "Any document that proves you are a Registered Military Dependent"
    "step.docUpload.nameChanged", // "If your name has changed since the document was issued, upload a 2nd document showing your official name change."
  ],
  [SubSegmentEnum.GOLD_STAR_FAMILY]: [
    "step.docUpload.acceptableUploads.military.goldStarFamily",
    "step.docUpload.nameChanged",
  ],
  [SubSegmentEnum.GRADUATE]: [],
  [SubSegmentEnum.FIREFIGHTER]: [],
  [SubSegmentEnum.POLICE]: [],
  [SubSegmentEnum.EMT]: [],
  [SubSegmentEnum.NURSE]: [],
  [SubSegmentEnum.DRIVER_LICENSE]: [],
  [SubSegmentEnum.GENERAL_IDENTITY]: [],
  [SubSegmentEnum.HYBRID_IDENTITY]: [],
  [SubSegmentEnum.HOME_BUYER]: [],
  [SubSegmentEnum.OTHER_MOVER]: [],
  [SubSegmentEnum.LICENSED_COSMETOLOGIST]: [
    "step.docUpload.acceptableUploads.licensedProfessional.license",
  ],
  [SubSegmentEnum.LICENSED_REAL_ESTATE_AGENT]: [
    "step.docUpload.acceptableUploads.licensedProfessional.license",
  ],
  [SubSegmentEnum.VETERINARIAN]: ["step.docUpload.acceptableUploads.licensedProfessional.license"],
  [SubSegmentEnum.SNAP_BENEFITS]: [
    "step.docUpload.acceptableUploads.lowIncome.programCard",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalLetter",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalScreenshot",
    "step.docUpload.acceptableUploads.lowIncome.programBankStatement",
  ],
  [SubSegmentEnum.OTHER_GOVERNMENT_ASSISTANCE]: [
    "step.docUpload.acceptableUploads.lowIncome.program",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalLetter",
    "step.docUpload.acceptableUploads.lowIncome.programApprovalScreenshot",
    "step.docUpload.acceptableUploads.lowIncome.programBankStatement",
  ],
  [SubSegmentEnum.CHILD_CARE_WORKER]: [
    "step.docUpload.acceptableUploads.licensedProfessional.license",
  ],
  [SubSegmentEnum.LIBRARIAN]: [],
  [SubSegmentEnum.INTERIOR_DESIGNER]: [],
  [SubSegmentEnum.ARCHITECT]: [],
  [SubSegmentEnum.GENERAL_CONTRACTOR]: [],
  [SubSegmentEnum.NUTRITION_PROFESSIONAL]: [
    "step.docUpload.acceptableUploads.licensedProfessional.license",
  ],
};

const SegmentUploadInfo: Record<SegmentEnum, NestedMessagePaths[]> = {
  [SegmentEnum.STUDENT]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.institutionName",
    "step.docUpload.uploadInfo.enrollmentProof",
    "step.docUpload.uploadInfo.dateCurrentYear",
    "step.docUpload.uploadInfo.issueDate",
    "step.docUpload.uploadInfo.expiration",
  ],
  [SegmentEnum.TEACHER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.institutionName",
    "step.docUpload.uploadInfo.dateCurrentYear",
  ],
  [SegmentEnum.FIRST_RESPONDER]: ["step.docUpload.uploadInfo.fullName"],
  [SegmentEnum.MEDICAL]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.medical.medicalRole",
    "step.docUpload.uploadInfo.medical.validDate",
  ],
  [SegmentEnum.MILITARY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.military.serviceBranch",
  ],
  [SegmentEnum.IDENTITY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.dateCurrentYear",
  ],
  [SegmentEnum.MEMBER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.organization",
    "step.docUpload.uploadInfo.affiliation",
  ],
  [SegmentEnum.SENIOR]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.senior.birthDate",
  ],
  [SegmentEnum.AGE]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.age.birthDate",
  ],
  [SegmentEnum.EMPLOYMENT]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.employment.company",
    "step.docUpload.uploadInfo.employment.currentAffiliation",
    "step.docUpload.uploadInfo.medical.validDate",
  ],
  [SegmentEnum.LICENSED_PROFESSIONAL]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.licensedProfessional.licenseStatus",
    "step.docUpload.uploadInfo.licensedProfessional.validDate",
  ],
  [SegmentEnum.RECENT_MOVER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.recentMover.address",
    "step.docUpload.uploadInfo.recentMover.validDate",
  ],
  [SegmentEnum.LOW_INCOME]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.lowIncome.validDate",
  ],
};

const SubSegmentUploadInfo: Record<SubSegmentEnum, NestedMessagePaths[]> = {
  [SubSegmentEnum.ACTIVE_DUTY]: ["step.docUpload.uploadInfo.military.activeDuty.status"],
  [SubSegmentEnum.VETERAN]: ["step.docUpload.uploadInfo.military.veteran.status"],
  [SubSegmentEnum.RESERVIST]: ["step.docUpload.uploadInfo.military.reservist.status"],
  [SubSegmentEnum.MILITARY_RETIREE]: ["step.docUpload.uploadInfo.military.militaryRetiree.status"],
  [SubSegmentEnum.MILITARY_FAMILY]: [
    // Overridden in SubSegmentUploadInfoOverrides
  ],
  [SubSegmentEnum.GOLD_STAR_FAMILY]: [
    // Overridden in SubSegmentUploadInfoOverrides
  ],
  [SubSegmentEnum.GRADUATE]: [
    // Overridden in SubSegmentUploadInfoOverrides
  ],
  [SubSegmentEnum.FIREFIGHTER]: ["step.docUpload.uploadInfo.firstResponder.firefighter.status"],
  [SubSegmentEnum.POLICE]: ["step.docUpload.uploadInfo.firstResponder.police.status"],
  [SubSegmentEnum.EMT]: ["step.docUpload.uploadInfo.firstResponder.emt.status"],
  [SubSegmentEnum.NURSE]: [],
  [SubSegmentEnum.DRIVER_LICENSE]: [],
  [SubSegmentEnum.GENERAL_IDENTITY]: [],
  [SubSegmentEnum.HYBRID_IDENTITY]: [],
  [SubSegmentEnum.HOME_BUYER]: [],
  [SubSegmentEnum.OTHER_MOVER]: [],
  [SubSegmentEnum.LICENSED_COSMETOLOGIST]: [],
  [SubSegmentEnum.LICENSED_REAL_ESTATE_AGENT]: [],
  [SubSegmentEnum.VETERINARIAN]: [],
  [SubSegmentEnum.SNAP_BENEFITS]: [],
  [SubSegmentEnum.OTHER_GOVERNMENT_ASSISTANCE]: [],
  [SubSegmentEnum.CHILD_CARE_WORKER]: [],
  [SubSegmentEnum.LIBRARIAN]: [],
  [SubSegmentEnum.INTERIOR_DESIGNER]: [],
  [SubSegmentEnum.ARCHITECT]: [],
  [SubSegmentEnum.GENERAL_CONTRACTOR]: [],
  [SubSegmentEnum.NUTRITION_PROFESSIONAL]: [],
};

// Subsegment messages are usually added to the segment messages, but some
// should replace the main segment messages, they go here.
const SubSegmentUploadInfoOverrides: Partial<Record<SubSegmentEnum, NestedMessagePaths[]>> = {
  // MILITARY_FAMILY is overridden to not show branch of service.
  [SubSegmentEnum.MILITARY_FAMILY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.military.militaryFamily.status",
  ],
  [SubSegmentEnum.GOLD_STAR_FAMILY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.military.goldStarFamily.status",
  ],
  [SubSegmentEnum.GRADUATE]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.institutionName",
    "step.docUpload.uploadInfo.student.graduate.degreeConferral",
  ],
};

// Subsegment suggested docs are usually added to the segment's, but some should
// replace the main segment messages (mirrors SubSegmentUploadInfoOverrides), they go here.
const SubSegmentAcceptableUploadsOverrides: Partial<Record<SubSegmentEnum, NestedMessagePaths[]>> =
  {
    [SubSegmentEnum.GRADUATE]: [
      "step.docUpload.acceptableUploads.student.graduate.transcript",
      "step.docUpload.acceptableUploads.student.graduate.diploma",
      "step.docUpload.acceptableUploads.student.graduate.letterOfCompletion",
      "step.docUpload.acceptableUploads.student.graduate.alumniCard",
    ],
  };

export function getAcceptableUploads(
  segment: Segment,
  subSegment: SubSegment,
): NestedMessagePaths[] {
  return (
    SubSegmentAcceptableUploadsOverrides[subSegment] || [
      ...new Set([
        ...(SubSegmentAcceptableUploads[subSegment] || []),
        ...(SegmentAcceptableUploads[segment] || []),
      ]),
    ]
  );
}

export function getUploadInfo(segment: Segment, subSegment: SubSegment): NestedMessagePaths[] {
  return (
    SubSegmentUploadInfoOverrides[subSegment] || [
      ...new Set([
        ...(SubSegmentUploadInfo[subSegment] || []),
        ...(SegmentUploadInfo[segment] || []),
      ]),
    ]
  );
}
